/**
 * Music-domain pure functions: lyric generation, Music DNA analysis, prompt
 * construction, genre mapping, and genre-compatibility scoring.
 *
 * SIMULATED. Everything here is deterministic client-side generation standing
 * in for real AI services:
 *
 *   - generateLyrics()  -> a lyrics model
 *   - analyzeMusicDNA() -> real audio/playlist analysis
 *   - buildDnaPrompt(), buildMixerPrompt(), buildMusicDna() -> a prompt
 *     optimizer
 *   - genreCompatibility() -> a trained compatibility model
 *
 * These are intentionally preserved as simulations (Phase 2 does not connect
 * real services). They live behind src/api/generationApi.ts so Phase 5 can
 * swap the implementation without touching the UI.
 *
 * The URL predicates are the exception: detectMusicSource() and isLikelySpotifyUrl() are
 * REAL — they answer a question about a string, which is a question this app can answer.
 * What they cannot do is read the track behind the link.
 *
 * All functions are pure and deterministic: same input, same output. That is
 * what makes them unit-testable and what keeps the UI from flickering between
 * renders.
 */
import {
  DNA_ATMOSPHERES,
  DNA_GENRES,
  DNA_INSTRUMENTS,
  DNA_MOODS,
  DNA_PRODUCTION,
  DNA_VOCALS,
  GM_AUDIENCES,
  GM_STRUCTURES,
} from '../data/music';
import { hashString } from './hash';
import type { GenreCompatibility, MusicDNA, MusicSource } from '../types/domain';

// ---------------------------------------------------------------------------
// Lyrics
// ---------------------------------------------------------------------------

/** Builds a full lyric sheet from a prompt and a genre name. */
export function generateLyrics(promptText: string, genreName: string): string {
  if (genreName.trim().toLowerCase() === 'lo-fi instrumental') return '';

  const theme = promptText.trim().replace(/\.$/, '') || 'this moment';
  const short = theme.length > 40 ? theme.slice(0, 40).trim() + '…' : theme;

  const verseStarters = [
    `Thinking about ${short.toLowerCase()}`,
    `Somewhere between the noise and the quiet`,
    `I keep coming back to ${short.toLowerCase()}`,
  ];

  return [
    '[Verse 1]',
    `${verseStarters[0]}`,
    "Every second feels like it's ours to keep",
    'Chasing daylight, holding on a little tighter',
    "This is the story I don't want to sleep through",
    '',
    '[Chorus]',
    `This is ${short.toLowerCase()}, wide awake and free`,
    genreName.trim()
      ? `A ${genreName.toLowerCase()} kind of feeling, just you and me`
      : 'A feeling you can hold onto, just you and me',
    "We don't need a reason, we just need tonight",
    'Turn it up, turn it up, until it feels right',
    '',
    '[Verse 2]',
    `${verseStarters[1]}`,
    'Every little detail, painted in the light',
    'If this is a moment, let it never end',
    '',
    '[Bridge]',
    "Maybe it's simple, maybe it's true",
    'The best songs always start with something like you',
  ].join('\n');
}

// ---------------------------------------------------------------------------
// Music DNA (Composer+)
// ---------------------------------------------------------------------------

/*
  THE SERVICE A REFERENCE LINK MAY COME FROM.

  DETECTION ONLY. Knowing whether a URL is a Spotify link is a question about a string,
  so the predicate below is real and complete. Reading the track behind the link is a
  different question — see analyzeMusicDNA below and api/generationApi.musicDnaSupport().

  `MusicSource` itself is in types/domain because it is part of the analysis request.
*/

/** The service the Music DNA field accepts. */
export const MUSIC_SOURCES: readonly MusicSource[] = ['spotify'];

/** Human name for a source, for copy and for error messages. */
export const MUSIC_SOURCE_LABELS: Record<MusicSource, string> = {
  spotify: 'Spotify',
};

const SPOTIFY_MUSIC_DNA_HOSTS = new Set(['open.spotify.com', 'spotify.link']);

/** True when the URL is an HTTPS link on an explicitly supported Spotify host. */
export function isLikelySpotifyUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return (
      u.protocol === 'https:' &&
      !u.username &&
      !u.password &&
      !u.port &&
      SPOTIFY_MUSIC_DNA_HOSTS.has(u.hostname.toLowerCase())
    );
  } catch {
    return false;
  }
}

/**
 * Which service a pasted link is from, or null when it is neither.
 *
 * The one function the UI and the store should call: a screen that asked
 * `isLikelySpotifyUrl(url)` would know the link is acceptable without naming the service,
 * and the source is what the analysis request has to carry.
 */
export function detectMusicSource(url: string): MusicSource | null {
  if (isLikelySpotifyUrl(url)) return 'spotify';
  return null;
}

/**
 * Derives a MusicDNA profile from a reference URL.
 *
 * SIMULATED: the profile is hashed from the URL string, so the same link always
 * "analyses" to the same result. Nothing is fetched from Spotify and no audio is
 * examined. A backend would run real analysis and return this same shape — see
 * api/generationApi.describeMusicDnaBlockers() for what that needs, and
 * docs/api-contract.md §16 for the endpoint.
 */
export function analyzeMusicDNA(url: string, source?: MusicSource): MusicDNA {
  const detected = source ?? detectMusicSource(url);
  const h = hashString(url);
  const pick = (arr: readonly string[], offset: number) => arr[(h + offset) % arr.length];
  return {
    genre: pick(DNA_GENRES, 1),
    mood: pick(DNA_MOODS, 2),
    energy: 25 + (h % 70),
    bpm: 68 + (h % 112),
    // The prototype called this `vocal`; the domain model calls it `vocals`, the
    // name used for the same concept everywhere else.
    vocals: pick(DNA_VOCALS, 3),
    production: pick(DNA_PRODUCTION, 4),
    instruments: [
      pick(DNA_INSTRUMENTS, 5),
      pick(DNA_INSTRUMENTS, 6),
      pick(DNA_INSTRUMENTS, 7),
    ].filter((v, i, a) => a.indexOf(v) === i),
    atmosphere: pick(DNA_ATMOSPHERES, 8),
    sourceUrl: url,
    ...(detected ? { source: detected } : null),
  };
}

/** Human label for the 0-100 inspiration-strength slider. */
export function strengthLabel(v: number): string {
  if (v < 34) return 'Loosely inspired';
  if (v < 67) return 'Balanced inspiration';
  return 'Closely inspired';
}

/**
 * Composes a generation prompt from a DNA profile plus a chosen creative
 * direction. `variant` rerolls the phrasing without changing the inputs.
 */
export function buildDnaPrompt(
  dna: MusicDNA,
  directionLabel: string,
  strength: number,
  variant = 0
): string {
  const closeness = strengthLabel(strength).toLowerCase();
  const instruments = dna.instruments.join(', ');

  if (directionLabel === 'Faithful interpretation') {
    const templates = [
      `A ${closeness} ${dna.genre.toLowerCase()} track with a ${dna.mood.toLowerCase()} mood, built around ${instruments}, evoking ${dna.atmosphere}. ${dna.production} production, ${dna.vocals.toLowerCase()}, around ${dna.bpm} BPM.`,
      `An original ${dna.genre.toLowerCase()} song, ${closeness} by the reference's ${dna.mood.toLowerCase()} feel — ${dna.production.toLowerCase()} production, ${dna.vocals.toLowerCase()}, driven by ${instruments}, set around ${dna.bpm} BPM and ${dna.atmosphere}.`,
    ];
    return templates[variant % templates.length];
  }
  if (directionLabel === 'Bold reimagining') {
    const templates = [
      `An original song that flips the energy of a ${dna.genre.toLowerCase()} reference into something bolder — same ${dna.mood.toLowerCase()} emotional core, but pushed toward a more intense, layered arrangement with ${instruments}, still set against ${dna.atmosphere}.`,
      `A bolder reinterpretation of a ${dna.genre.toLowerCase()} idea — heavier, more layered production built from ${instruments}, keeping the ${dna.mood.toLowerCase()} core but turning up the intensity, still rooted in ${dna.atmosphere}.`,
    ];
    return templates[variant % templates.length];
  }
  const templates = [
    `A stripped-down, minimalist take inspired by a ${dna.genre.toLowerCase()} reference — just ${dna.instruments[0]} and space, ${dna.mood.toLowerCase()} and unhurried, quietly echoing ${dna.atmosphere}.`,
    `A quiet, minimal reimagining — mostly ${dna.instruments[0]} and silence, ${dna.mood.toLowerCase()} in tone, taking its time inside ${dna.atmosphere}.`,
  ];
  return templates[variant % templates.length];
}

// ---------------------------------------------------------------------------
// Genre mapping
// ---------------------------------------------------------------------------

/** Maps a DNA genre onto one of the app's generation genres. */
export function mapDnaGenreToAppGenre(dnaGenre = ''): string {
  const g = dnaGenre.toLowerCase();
  if (g.includes('hip hop')) return 'Hip hop';
  if (g.includes('afrobeat')) return 'Afrobeat';
  if (g.includes('house') || g.includes('synth')) return 'Synthwave';
  if (g.includes('folk') || g.includes('acoustic')) return 'Acoustic pop';
  if (g.includes('lo-fi') || g.includes('dream')) return 'Lo-fi instrumental';
  return 'Cinematic';
}

/** Maps a Genre Mixer genre onto one of the app's generation genres. */
export function mapMixerGenreToAppGenre(genre1 = ''): string {
  const g = genre1.toLowerCase();
  if (g.includes('hip hop')) return 'Hip hop';
  if (g.includes('afrobeat') || g.includes('afro pop') || g.includes('afropop'))
    return 'Afrobeat';
  if (
    g.includes('house') ||
    g.includes('edm') ||
    g.includes('synthwave') ||
    g.includes('dancehall')
  )
    return 'Synthwave';
  if (g.includes('country') || g.includes('gospel') || g.includes('blues') || g.includes('pop'))
    return 'Acoustic pop';
  if (g.includes('lo-fi') || g.includes('jazz') || g.includes('classical'))
    return 'Lo-fi instrumental';
  return 'Cinematic';
}

// ---------------------------------------------------------------------------
// Genre Mixer (Composer+)
// ---------------------------------------------------------------------------

/**
 * Scores how well two genres blend, 45-98, with a human label.
 * Returns null until both genres are chosen.
 */
export function genreCompatibility(genre1: string, genre2: string): GenreCompatibility | null {
  if (!genre1 || !genre2) return null;
  const h = hashString(genre1 + '::' + genre2);
  const score = 45 + (h % 54); // 45–98
  let label = 'Experimental';
  if (score >= 85) label = 'Excellent Blend';
  else if (score >= 70) label = 'Very Compatible';
  else if (score >= 50) label = 'Moderately Compatible';
  return { score, label };
}

/** Picks a BPM inside the range implied by a tempo label. */
export function tempoToBpm(tempo: string, seed = 0): number {
  const ranges: Record<string, [number, number]> = {
    Slow: [64, 88],
    Medium: [92, 116],
    Fast: [120, 144],
  };
  const [min, max] = ranges[tempo] || ranges.Medium;
  return min + (seed % (max - min));
}

/** Composes a generation prompt from the full Genre Mixer settings. */
export function buildMixerPrompt({
  genre1,
  genre2,
  genre3,
  mood,
  tempo,
  language,
  vocal,
  length,
  energy,
  variation,
  variant = 0,
}: {
  genre1: string;
  genre2: string;
  genre3?: string;
  mood: string;
  tempo: string;
  language: string;
  vocal: string;
  length: string;
  energy: number;
  variation?: string | null;
  variant?: number;
}): string {
  if (!genre1 || !genre2) return '';
  const h = hashString(
    `${genre1}${genre2}${genre3}${mood}${tempo}${language}${vocal}${length}${variation}${variant}`
  );
  const bpm = tempoToBpm(tempo, h);
  const genreBlend = genre3 ? `${genre1}, ${genre2}, and ${genre3}` : `${genre1} and ${genre2}`;
  const instrumentSets = [
    'energetic log drums, rhythmic percussion, melodic synth chords, and uplifting vocal melodies',
    'layered guitars, driving bass, tight percussion, and soaring lead vocals',
    'warm keys, subtle strings, deep bass, and intimate vocal phrasing',
    'punchy 808s, crisp hi-hats, atmospheric pads, and confident vocal delivery',
  ];
  const instruments = instrumentSets[h % instrumentSets.length];

  const variationTails: Record<string, string> = {
    'Commercial Radio':
      'Keep the production polished and radio-ready, with a strong, memorable hook.',
    'Festival Mix': 'Build toward big, energetic drops designed for a live festival crowd.',
    'Acoustic Version':
      'Strip the arrangement down to mostly acoustic instruments, warm and intimate.',
  };
  const variationTail = (variation ? variationTails[variation] : '') || '';

  const vocalLine =
    vocal === 'Instrumental'
      ? 'Keep the track fully instrumental, no vocals.'
      : `Feature ${vocal.toLowerCase()} vocals`;
  const langLine =
    language === 'Instrumental' || vocal === 'Instrumental' ? '' : ` sung in ${language}`;

  const fullPrompt = [
    `Create an original ${genreBlend} song.`,
    `Use ${instruments} at approximately ${bpm} BPM, with a ${mood.toLowerCase()} mood and ${energy}% energy.`,
    `${vocalLine}${langLine}, running about ${length}. ${variationTail}`,
    `Create an entirely original composition. Never copy melodies or copyrighted material.`,
  ].join(' ');

  const finalSentenceStart = fullPrompt.lastIndexOf('. ');
  return finalSentenceStart < 0 ? fullPrompt.trim() : fullPrompt.slice(0, finalSentenceStart + 1).trim();
}

/**
 * Builds the analytical "DNA" panel shown beside the Genre Mixer.
 * Returns null until both genres are chosen.
 */
export function buildMusicDna({
  genre1,
  genre2,
  genre3,
  mood,
  tempo,
  energy,
  variation,
}: {
  genre1: string;
  genre2: string;
  genre3?: string;
  mood: string;
  tempo: string;
  energy: number;
  variation?: string | null;
}) {
  if (!genre1 || !genre2) return null;
  const h = hashString(`${genre1}${genre2}${genre3}${mood}${tempo}${variation}`);
  const bpm = tempoToBpm(tempo, h);
  const danceability = Math.min(
    99,
    Math.round(energy * 0.6 + (tempo === 'Fast' ? 30 : tempo === 'Medium' ? 18 : 6))
  );
  const commercial = Math.min(99, 50 + (h % 45));
  return {
    primaryGenre: genre1,
    secondaryGenre: genre2,
    energy,
    instrumentation: DNA_INSTRUMENTS[(h + 3) % DNA_INSTRUMENTS.length],
    production: DNA_PRODUCTION[(h + 5) % DNA_PRODUCTION.length],
    structure: GM_STRUCTURES[h % GM_STRUCTURES.length],
    atmosphere: DNA_ATMOSPHERES[(h + 7) % DNA_ATMOSPHERES.length],
    danceability,
    emotionalTone: mood,
    audience: GM_AUDIENCES[h % GM_AUDIENCES.length],
    commercial,
    bpm,
  };
}
